import { curvaParpadeo, PARPADEO_MS } from './idle';
import {
  entre,
  GRADOS_A_RAD,
  Pulso,
  RuidoSuave,
  Sacada,
  signo,
  VIDA_DEFECTOS,
  type Rng,
  type VidaOpts,
} from './vida';

/**
 * Gestos acoplados al habla (tanda 8, pieza 2).
 *
 * Hermana de `habla.ts`: lee el mismo nivel suavizado que mueve la boca y
 * lo traduce a lo que hace el resto de la cara cuando alguien habla —
 * asentir en los énfasis, levantar las cejas, mover la cabeza, parpadear
 * en las pausas, desviar la mirada al arrancar una frase.
 *
 * **Son gestos, no emociones.** Nada de esto mira qué expresión está
 * aplicada ni cambia cuál se muestra: la expresión la decide el backend.
 * Un énfasis produce el mismo cabeceo en `ALEGRIA` que en `TRISTEZA`, y
 * se suma encima de lo que la expresión ya tenga.
 *
 * Como las otras capas, sólo produce desplazamientos; el que los compone
 * es el `ThreeRigAdapter`.
 */

/** Lo que aportan los gestos en un instante. */
export interface GestosOffsets {
  /** Cabeceo en radianes. Negativo = hacia abajo (asentimiento). */
  cabezaPitch: number;
  /** Giro en radianes. Positivo = el avatar mira a su derecha. */
  cabezaYaw: number;
  /** Inclinación lateral en radianes. */
  cabezaRoll: number;
  /** Peso que se **suma** a `browInnerUp` y `browOuterUp*`. */
  cejas: number;
  /** Cierre extra de párpados, `0..1`, igual que el de la idle. */
  parpadeo: number;
  /** Desvío de mirada, se suma a `mirada_x`. */
  miradaX: number;
  /** Desvío de mirada, se suma a `mirada_y`. */
  miradaY: number;
}

export const GESTOS_REPOSO: Readonly<GestosOffsets> = {
  cabezaPitch: 0,
  cabezaYaw: 0,
  cabezaRoll: 0,
  cejas: 0,
  parpadeo: 0,
  miradaX: 0,
  miradaY: 0,
};

/**
 * Detector de énfasis sobre el nivel de la voz.
 *
 * Un énfasis es un pico: el nivel supera su media móvil por un factor
 * (~1,5). Después de disparar espera un período refractario, o dispararía
 * en cada sílaba acentuada.
 *
 * La media se reinicia en cada enunciado nuevo —después de un silencio
 * largo— y durante un calentamiento corto sigue rápido al nivel sin
 * detectar: recién arrancada no representa a la voz, y el primer golpe de
 * cualquier frase contaría como énfasis.
 */
export class DetectorEnfasis {
  private media = 0;
  private calentamientoMs = 0;
  /** Silencio acumulado. Arranca "largo": lo primero es un enunciado nuevo. */
  private silencioMs = Number.POSITIVE_INFINITY;
  private desdeUltimoMs = Number.POSITIVE_INFINITY;
  /** Si el nivel ya estaba sobre el umbral: se dispara al cruzarlo, no mientras. */
  private arriba = false;

  constructor(private vida: Readonly<VidaOpts> = VIDA_DEFECTOS) {}

  configurar(vida: Readonly<VidaOpts>): void {
    this.vida = vida;
  }

  reiniciar(): void {
    this.media = 0;
    this.calentamientoMs = 0;
    this.silencioMs = Number.POSITIVE_INFINITY;
    this.desdeUltimoMs = Number.POSITIVE_INFINITY;
    this.arriba = false;
  }

  /**
   * @param nivel nivel de la voz, `0..1`.
   * @param umbralVoz por debajo, es silencio (el umbral de pausa del habla).
   * @returns si en este frame hay un énfasis.
   */
  update(deltaMs: number, nivel: number, umbralVoz: number): boolean {
    const v = this.vida;
    this.desdeUltimoMs += deltaMs;

    if (nivel < umbralVoz) {
      this.silencioMs += deltaMs;
      this.arriba = false;
      return false;
    }

    if (this.silencioMs >= v.pausaEnunciadoMs) {
      this.media = nivel;
      this.calentamientoMs = v.enfasisCalentamientoMs;
    }
    this.silencioMs = 0;

    // Se compara contra la media **anterior**: incluir el pico en su
    // propia referencia le resta sensibilidad.
    const umbral = this.media * v.enfasisFactor;
    const calentando = this.calentamientoMs > 0;
    const tau = calentando ? Math.min(v.enfasisMediaMs, 60) : v.enfasisMediaMs;
    this.media += (nivel - this.media) * (1 - Math.exp(-deltaMs / Math.max(1, tau)));
    if (calentando) {
      this.calentamientoMs -= deltaMs;
      return false;
    }

    const sobre = nivel > umbral && nivel >= v.enfasisNivelMin;
    const dispara = sobre && !this.arriba && this.desdeUltimoMs >= v.enfasisRefractarioMs;
    this.arriba = sobre;
    if (dispara) this.desdeUltimoMs = 0;
    return dispara;
  }
}

export interface CapaGestosOpts {
  rng?: Rng;
  vida?: Readonly<VidaOpts>;
}

/**
 * Genera los gestos de cada frame a partir del nivel de la voz.
 *
 * Eventos que reconoce, todos sobre el nivel y el umbral de pausa que ya
 * usa la boca:
 *
 * - **Énfasis** (`DetectorEnfasis`): asentimiento y pulso de cejas.
 * - **Inicio de pausa** (silencio sostenido `pausaMinMs`): parpadeo con
 *   probabilidad.
 * - **Inicio de enunciado** (voz después de `pausaEnunciadoMs` de
 *   silencio, o al arrancar el audio): desvío breve de mirada, que vuelve
 *   a cámara al vencer su plazo o al terminar la frase.
 * - **Mientras suena el audio**: deriva lenta de cabeza.
 */
export class CapaGestos {
  private readonly rng: Rng;
  private vida: Readonly<VidaOpts>;
  private readonly detector: DetectorEnfasis;

  private readonly asentimiento = new Pulso();
  private readonly cejas = new Pulso();
  private readonly ruidoYaw: RuidoSuave;
  private readonly ruidoRoll: RuidoSuave;
  private fundidoDeriva = 0;

  private readonly mirada = new Sacada();
  /** Cuánto falta para que el desvío de inicio vuelva a cámara. */
  private miradaRestanteMs = 0;

  private faseParpadeo: number | null = null;

  private silencioMs = Number.POSITIVE_INFINITY;
  /** Si la pausa en curso ya se contó como tal. */
  private pausaContada = true;

  private readonly salida: GestosOffsets = { ...GESTOS_REPOSO };

  constructor(opts: CapaGestosOpts = {}) {
    this.rng = opts.rng ?? Math.random;
    this.vida = opts.vida ?? VIDA_DEFECTOS;
    this.detector = new DetectorEnfasis(this.vida);
    this.ruidoYaw = new RuidoSuave(this.rng);
    this.ruidoRoll = new RuidoSuave(this.rng);
  }

  configurar(vida: Readonly<VidaOpts>): void {
    this.vida = vida;
    this.detector.configurar(vida);
  }

  /** Si algún gesto todavía se está moviendo o volviendo a reposo. */
  get activo(): boolean {
    return (
      this.fundidoDeriva > 1e-3 ||
      this.asentimiento.activo ||
      this.cejas.activo ||
      this.faseParpadeo !== null ||
      this.mirada.x !== 0 ||
      this.mirada.destinoX !== 0
    );
  }

  /**
   * @param nivel nivel suavizado de la voz (`HablaOffsets.nivel`).
   * @param hablando si hay un audio sonando.
   * @param umbralPausa el umbral de pausa de la capa de habla.
   */
  update(deltaMs: number, nivel: number, hablando: boolean, umbralPausa: number): GestosOffsets {
    const v = this.vida;
    const voz = hablando && nivel >= umbralPausa;

    if (voz) {
      if (this.silencioMs >= v.pausaEnunciadoMs) this.iniciarEnunciado();
      this.silencioMs = 0;
      this.pausaContada = false;
    } else {
      this.silencioMs += deltaMs;
      if (!this.pausaContada && this.silencioMs >= v.pausaMinMs) {
        this.pausaContada = true;
        if (hablando && this.rng() < v.parpadeoPausaProb) this.faseParpadeo ??= 0;
      }
      if (this.silencioMs >= v.pausaEnunciadoMs) this.volverACamara();
    }

    if (this.detector.update(deltaMs, hablando ? nivel : 0, umbralPausa)) {
      this.asentimiento.disparar(
        entre(this.rng, v.asentimientoMinGrados, v.asentimientoMaxGrados) * GRADOS_A_RAD,
      );
      this.cejas.disparar(entre(this.rng, v.cejasPulsoMin, v.cejasPulsoMax));
    }

    if (this.miradaRestanteMs > 0) {
      this.miradaRestanteMs -= deltaMs;
      if (this.miradaRestanteMs <= 0) this.volverACamara();
    }
    if (!hablando) this.volverACamara();
    this.mirada.update(deltaMs);

    // La deriva entra y sale con el audio, no con cada sílaba: una pausa
    // entre frases no frena la cabeza.
    const objetivo = hablando ? 1 : 0;
    this.fundidoDeriva +=
      (objetivo - this.fundidoDeriva) * (1 - Math.exp(-deltaMs / Math.max(1, v.derivaFundidoMs)));
    if (this.fundidoDeriva < 1e-4) this.fundidoDeriva = 0;
    const yaw = this.ruidoYaw.update(deltaMs, v.derivaHz);
    const roll = this.ruidoRoll.update(deltaMs, v.derivaHz);

    const s = this.salida;
    // Hacia abajo: pitch negativo. Sin `-0` en reposo.
    const asentir = this.asentimiento.update(
      deltaMs,
      v.asentimientoAtaqueMs,
      v.asentimientoCaidaMs,
    );
    s.cabezaPitch = asentir === 0 ? 0 : -asentir;
    s.cabezaYaw = yaw * v.derivaYawGrados * GRADOS_A_RAD * this.fundidoDeriva;
    s.cabezaRoll = roll * v.derivaRollGrados * GRADOS_A_RAD * this.fundidoDeriva;
    s.cejas = this.cejas.update(deltaMs, v.cejasAtaqueMs, v.cejasCaidaMs);
    s.parpadeo = this.avanzarParpadeo(deltaMs);
    s.miradaX = this.mirada.x;
    s.miradaY = this.mirada.y;
    return s;
  }

  /** Vuelve todo a reposo de inmediato. Para las capturas. */
  reiniciar(): void {
    this.asentimiento.reiniciar();
    this.cejas.reiniciar();
    this.mirada.reiniciar();
    this.miradaRestanteMs = 0;
    this.fundidoDeriva = 0;
    this.faseParpadeo = null;
    this.silencioMs = Number.POSITIVE_INFINITY;
    this.pausaContada = true;
    this.detector.reiniciar();
    Object.assign(this.salida, GESTOS_REPOSO);
  }

  private iniciarEnunciado(): void {
    const v = this.vida;
    this.mirada.ir(
      signo(this.rng) * entre(this.rng, v.miradaInicioMin, v.miradaInicioMax),
      0,
      entre(this.rng, v.sacadaMinMs, v.sacadaMaxMs),
    );
    this.miradaRestanteMs = entre(this.rng, v.miradaInicioMinMs, v.miradaInicioMaxMs);
  }

  private volverACamara(): void {
    this.miradaRestanteMs = 0;
    if (this.mirada.destinoX === 0 && this.mirada.destinoY === 0) return;
    const v = this.vida;
    this.mirada.ir(0, 0, entre(this.rng, v.sacadaMinMs, v.sacadaMaxMs));
  }

  private avanzarParpadeo(deltaMs: number): number {
    if (this.faseParpadeo === null) return 0;
    this.faseParpadeo += deltaMs / PARPADEO_MS;
    if (this.faseParpadeo >= 1) {
      this.faseParpadeo = null;
      return 0;
    }
    return curvaParpadeo(this.faseParpadeo);
  }
}
